import { useEffect, useState, useCallback } from "react";
import {
  BookingDetailsDTO,
  getAllBookingsWithDetails,
  OrderDetailsDTO,
} from "./bookingService";
import axios from "axios";
import { DateRange } from "../CampSites/types";
import AllBookingsList from "./AllBookingsList";
import NewBookingForm from "./NewBookingForm";
import SimpleBookingForm from "./SimpleBookingForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS, Locale } from "date-fns/locale";
import BookingConfirmation from "./BookingConfirmation"; // Import the BookingConfirmation component

export interface ManagerBookingRequest {
  PackageId: number | null;

  PricingModelId: number | null;
  PackageName?: string;
  ArrivalDate: Date | string | null;
  DepartureDate: Date | string | null;
  NumberOfPersons: number;
  IsBusinessUser: boolean;
  CompanyName? : string | null;
  Email: string;
  CustomerName: string;
  GuestNames?: string[];
  PhoneNumber: string;
  Address: string;
  City: string;
  State: string;
  ZipCode: string;
  Country: string;
  SessionId: string;
  BusinessCode?: string;
  CustomerId?: number;
  BusinessUserId?: number;
  PrivateUserId?: number;
  TotalPrice: number;
  PricingModelDescription: string;
  BookingDescription: string;
  ChaletType?: number | null; // 0 = Big, 1 = Small
  ChaletId?: number | null; // Specific chalet ID for the booking
}

const NewBookingManager = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "defaultApiUrl";
  const token = localStorage.getItem("accessToken");
  const [orders, setOrders] = useState<OrderDetailsDTO[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [bookings, setBookings] = useState<BookingDetailsDTO[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showForm, setShowForm] = useState<boolean>(false);
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRange>({ startDate: null, endDate: null });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | "">("");
  const [editingBooking, setEditingBooking] = useState<BookingDetailsDTO | null>(null);
  const [filter, setFilter] = useState<"all" | "private" | "business" | "dateRange">("all");
  const [currentView, setCurrentView] = useState<"list" | "form" | "confirmation">("list"); // Add "confirmation" to the view state
  const [showDropdown, setShowDropdown] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [locale, setLocale] = useState<Locale>(enUS);
  const [orderDetails, setOrderDetails] = useState<OrderDetailsDTO | null>(null); // Add state for order details

  // Helper: sort orders by newest first using createdAt, fallback to first booking's arrivalDate
  const sortOrdersDesc = (arr: OrderDetailsDTO[]) => {
    const getTime = (o: OrderDetailsDTO) => {
      const created = o.createdAt ? new Date(o.createdAt).getTime() : 0;
      if (created) return created;
      const firstArrival = o.bookings?.[0]?.arrivalDate
        ? new Date(o.bookings[0].arrivalDate).getTime()
        : 0;
      return firstArrival || 0;
    };
    return [...arr].sort((a, b) => getTime(b) - getTime(a));
  };

  const fetchBookings = useCallback(async () => {
    try {
      const response = await getAllBookingsWithDetails();
      console.log("Fetched response:", response);
      if (response.success) {
        if (Array.isArray(response.data)) {
          console.log("Setting orders data:", response.data);
          // Sort newest first on initial load
          setOrders(sortOrdersDesc(response.data));
          console.log("Orders data set successfully");
        } else {
          console.error("Response data is not an array:", response.data);
        }
        setError("");
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      setError(error.message);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]); // Run only once when the component mounts

  useEffect(() => {
    console.log("Orders state updated:", orders);
  }, [orders]); // Log orders whenever the state changes

  const handleAddOrder = async (data: ManagerBookingRequest[]) => {
    // Log the data object before validation
    console.log("Data to be sent:", data);

    // Retrieve sessionId from localStorage
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
        data.forEach((booking) => {
            booking.SessionId = sessionId;
        });
    } else {
        console.warn('No sessionId found in localStorage');
    }

    // Validation checks
    for (const booking of data) {
        if (booking.IsBusinessUser && !booking.BusinessCode) {
            alert("Please enter a valid business code.");
            return;
        }
    }

    try {
        console.log("Attempting to add booking..."); // Log when the function is called
        const response = await axios.post(
            `${apiUrl}/Booking/confirmBookingForManager`,
            JSON.stringify(data),
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        const newBooking: OrderDetailsDTO = response.data as OrderDetailsDTO;
    // Update UI immediately with newest-first ordering
    const appended = [...orders, newBooking];
    setOrders(sortOrdersDesc(appended));
        setShowForm(false);
        setCurrentView("confirmation"); // Set view to confirmation
        setOrderDetails(newBooking); // Set order details for confirmation
        fetchBookings();
    } catch (error) {
        console.error("Error adding booking", error);
    }
};
  const handleEditBooking = (booking: BookingDetailsDTO) => {
    setEditingBooking(booking);
    setShowForm(true);
    setCurrentView('form');
  };

  const handleFormVisibility = (visible: boolean) => {
    setFormVisible(visible);
  };

  const handleUpdateBooking = async (data: any) => {
    try {
      const response = await axios.put(`${apiUrl}/Booking/updateBooking`, data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const updatedBooking = response.data;
      setBookings(bookings.map(b => b.bookingId === editingBooking?.bookingId ? updatedBooking as BookingDetailsDTO : b));
      setShowForm(false);
      setCurrentView('confirmation'); // Set view to confirmation
      setOrderDetails(updatedBooking as OrderDetailsDTO); // Set order details for confirmation
      fetchBookings();
    } catch (error) {
      console.error("Error updating booking", error);
    }
  };

  const filteredBookings = orders.filter((booking) => {
    if (filter === "all") return true;
    if (filter === "private") return !booking.isBusinessUser;
    if (filter === "business") return booking.isBusinessUser;
    if (filter === "dateRange") {
      if (!dateRangeFilter.startDate || !dateRangeFilter.endDate) return true;
      const arrivalDate = booking.bookings[0]?.arrivalDate ? new Date(booking.bookings[0].arrivalDate) : null;
      const departureDate = booking.bookings[0]?.departureDate ? new Date(booking.bookings[0].departureDate) : null;
      console.log("Arrival Date:", arrivalDate);
      console.log("Departure Date:", departureDate);
      console.log("Start Date:", dateRangeFilter.startDate);
      console.log("End Date:", dateRangeFilter.endDate);
      return arrivalDate && departureDate && arrivalDate >= dateRangeFilter.startDate && departureDate <= dateRangeFilter.endDate;
    }
    return true;
  });

  console.log("Filter:", filter);
  console.log("Filtered bookings:", filteredBookings);

  const handleDateChange = (update: [Date | null, Date | null]) => {
    setDateRangeFilter({ startDate: update[0], endDate: update[1] });
  };

  return (
    <>
      <section className="manage-bookings-section">
        <div className="manage-bookings-container">
          <div className="manage-bookings-buttons mt-2 mb-4">
            {currentView === "list" && (
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingBooking(null);
                  setCurrentView("form");
                }}
              >
                Add Booking
              </button>
            )}
            {currentView === "list" && (
              <button
                onClick={() => {
                  // Soft refresh the list view
                  fetchBookings();
                }}
                className="ms-2"
              >
                Refresh
              </button>
            )}
            <button
              onClick={() => {
                setFilter("all");
                setCurrentView("list");
              }}
            >
              All
            </button>
            <button
              onClick={() => {
                setFilter("private");
                setCurrentView("list");
              }}
            >
              Private
            </button>
            <button
              onClick={() => {
                setFilter("business");
                setCurrentView("list");
              }}
            >
              Business
            </button>
            <button
              onClick={() => {
                setFilter("dateRange");
                setCurrentView("list");
              }}
            >
              Date Range
            </button>
          </div>
          {filter === "dateRange" && (
            <div className="form-group">
              <label htmlFor="dateRange">Arrival & Departure Dates</label>
              <div className="date-picker-wrapper">
                <DatePicker
                  selected={dateRangeFilter.startDate}
                  onChange={handleDateChange}
                  startDate={dateRangeFilter.startDate ?? undefined}
                  endDate={dateRangeFilter.endDate ?? undefined}
                  selectsRange
                  className="form-control"
                  placeholderText="Select dates"
                  dateFormat="EEE MMM d"
                  locale={locale}
                  calendarStartDay={1} // Start the calendar on Monday
                />
              </div>
            </div>
          )}
          <div className="dropdown-menu-container">
            <button className="dropdown-toggle" onClick={() => setShowDropdown(!showDropdown)}>Menu</button>
            {showDropdown && (
              <div className="dropdown-content">
                {currentView === 'list' ? (
                  <button onClick={() => { setShowForm(true); setEditingBooking(null); setCurrentView('form'); }}>Add Booking</button>
                ) : (
                  <button onClick={() => { setShowForm(false); setCurrentView('list'); }}>Cancel Add Booking</button>
                )}
                {currentView === 'list' && (
                  <>
                    <button onClick={() => { setFilter('all'); setCurrentView('list'); }}>All</button>
                    <button onClick={() => { setFilter('private'); setCurrentView('list'); }}>Private</button>
                    <button onClick={() => { setFilter('business'); setCurrentView('list'); }}>Business</button>
                    <button onClick={() => { setFilter('dateRange'); setCurrentView('list'); }}>Date Range</button>
                  </>
                )}
              </div>
            )}
          </div>
          {currentView === 'list' && (
            <AllBookingsList
              orders={filteredBookings}
              setOrders={setOrders}
              onView={() => { }}
              onEdit={handleEditBooking}
              filter={filter}
            />
          )}
          {currentView === 'form' && (
            <>
            <SimpleBookingForm apiUrl={apiUrl} />
            <NewBookingForm
              booking={editingBooking}
              setBooking={setEditingBooking}
              handleAddBooking={handleAddOrder} // Pass handleAddOrder as a prop
              handleUpdateBooking={handleUpdateBooking}
              handleEditBooking={handleEditBooking}
              onSubmit={() => setFormVisible(false)}
              onCancel={() => setFormVisible(false)}
              dateRange={dateRange}
              setDateRange={setDateRange}
              formVisible={formVisible}
              setFormVisible={handleFormVisibility} // Pass formVisible as a prop
            />
            </>
          )}
          {currentView === 'confirmation' && orderDetails && (
            <BookingConfirmation orderDetails={orderDetails} />
          )}
        </div>
      </section>
    </>
  );
};

export default NewBookingManager;